/**
 * UsageMeterBar — Reusable usage meter progress bar component
 *
 * Renders a single meter bar with:
 * - Current value, plan limit, and percentage text
 * - Visual states: normal, warning (≥80%), exceeded (100%)
 * - Uses --meter-* design tokens for consistent styling
 * - Optionally clickable (e.g. navigates to Settings billing tab)
 */

import * as React from 'react';
import { cn } from '../utils.js';

export type MeterStatus = 'normal' | 'warning' | 'exceeded';

export interface UsageMeterBarProps {
  /** Display label for the meter (e.g. "Integrations", "Records") */
  readonly label: string;
  /** Current consumption value */
  readonly current: number;
  /** Plan limit for this dimension */
  readonly limit: number;
  /** Formatted current value string (e.g. "4.2K", "1.3 GB"). Auto-formats from `current` if omitted. */
  readonly currentFormatted?: string;
  /** Formatted limit value string (e.g. "10K", "5 GB"). Auto-formats from `limit` if omitted. */
  readonly limitFormatted?: string;
  /** Billing dimension identifier for data attributes (e.g. "storage_gb") */
  readonly dimension?: string;
  /** Unit suffix for auto-formatting when currentFormatted/limitFormatted are omitted */
  readonly unit?: string;
  /** Click handler (e.g. navigates to billing) */
  readonly onClick?: () => void;
}

function formatValue(value: number, unit?: string): string {
  const suffix = unit ?? '';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M${suffix}`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K${suffix}`;
  return `${value}${suffix}`;
}

/**
 * The 80% / 100% thresholds, in ONE place.
 *
 * Exported because the header meter (`HeaderCapacityMeter`) renders the same dimensions in a
 * different shape and must agree with the panel about when amber starts. Two copies of "80"
 * would eventually disagree, and the disagreement would be invisible: one surface warning while
 * the other looks healthy is worse than neither warning.
 *
 * ⚠️ These are DISPLAY thresholds only. Enforcement lives in `subscription-gate.ts`, and the
 * decided commercial model makes capacity ADVISORY (DEBT-59) — nothing here refuses anything.
 */
export function getMeterStatus(current: number, limit: number): MeterStatus {
  if (limit <= 0) return 'normal';
  const percentage = (current / limit) * 100;
  if (percentage >= 100) return 'exceeded';
  if (percentage >= 80) return 'warning';
  return 'normal';
}

export function getPercentage(current: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min((current / limit) * 100, 100);
}

export function getFillColor(status: MeterStatus): string {
  switch (status) {
    case 'exceeded': return 'var(--meter-fill-critical)';
    case 'warning': return 'var(--meter-fill-warning)';
    default: return 'var(--meter-fill-normal)';
  }
}

export function UsageMeterBar({
  label,
  current,
  limit,
  currentFormatted,
  limitFormatted,
  dimension,
  unit,
  onClick,
}: UsageMeterBarProps): React.JSX.Element {
  const status = getMeterStatus(current, limit);
  const percentage = getPercentage(current, limit);
  const percentageText = limit > 0 ? `${Math.round(percentage)}%` : '—';
  const displayCurrent = currentFormatted ?? formatValue(current, unit);
  const displayLimit = limitFormatted ?? formatValue(limit, unit);

  // Capitalised local alias so JSX treats it as a component (lowercase strings
  // confuse the parser when the variable is dynamic). Cast so React's typing
  // is happy with both <button> and <div> targets.
  const Wrapper: React.ElementType = onClick ? 'button' : 'div';
  const wrapperProps: Record<string, unknown> = onClick
    ? { type: 'button', onClick }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={cn(
        'w-full text-left p-3 rounded-lg border border-border transition-colors',
        onClick && 'hover:bg-(--content-background) hover:border-(--border-strong)',
        onClick && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      )}
      aria-label={`${label}: ${displayCurrent} of ${displayLimit} used (${percentageText})`}
      {...(dimension ? { 'data-dimension': dimension } : {})}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span
          className="text-xs font-medium uppercase tracking-wide"
          style={{ color: 'var(--meter-label-color)' }}
        >
          {label}
          {status === 'warning' && (
            <span className="ml-1.5 text-3xs normal-case tracking-normal" style={{ color: 'var(--meter-fill-warning)' }}>
              · warning
            </span>
          )}
          {status === 'exceeded' && (
            <span className="ml-1.5 text-3xs normal-case tracking-normal" style={{ color: 'var(--meter-fill-critical)' }}>
              · at limit
            </span>
          )}
        </span>
        <span
          className="text-xs font-mono tabular-nums"
          style={{ color: 'var(--meter-value-color)' }}
        >
          {displayCurrent} / {displayLimit} · {percentageText}
        </span>
      </div>
      <div
        className="w-full relative overflow-hidden"
        style={{
          height: 'var(--meter-height)',
          background: 'var(--meter-track)',
          borderRadius: 'var(--meter-radius)',
        }}
      >
        <div
          className="absolute left-0 top-0 h-full transition-all duration-300 ease-out"
          style={{
            width: `${percentage}%`,
            background: getFillColor(status),
            borderRadius: 'inherit',
          }}
        />
      </div>
    </Wrapper>
  );
}
